import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllAnimations,
  createAnimation,
  updateAnimation,
  deleteAnimation,
  subscribeToAllAnimations
} from '../services/animations';
import { getAllCompanies } from '../services/companies';
import { getAllDevices } from '../services/devices';
import './AnimationManagement.css';

function AnimationManagement() {
  const navigate = useNavigate();
  const [animations, setAnimations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnimation, setEditingAnimation] = useState(null);
  const [filterType, setFilterType] = useState('all'); // all, global, specific

  // Form state
  const [formData, setFormData] = useState({
    companyId: '',
    animationUrl: '',
    targetDevices: [],
    isActive: true,
    expiresAt: ''
  });

  useEffect(() => {
    loadData();

    // Subscribe to real-time animation updates
    const unsubscribe = subscribeToAllAnimations((animationsData) => {
      setAnimations(animationsData);
    });

    return () => unsubscribe();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [animationsData, companiesData, devicesData] = await Promise.all([
        getAllAnimations(),
        getAllCompanies(),
        getAllDevices()
      ]);

      setAnimations(animationsData);
      setCompanies(companiesData);
      setDevices(devicesData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const animationData = {
        companyId: formData.companyId,
        animationUrl: formData.animationUrl,
        targetDevices: formData.targetDevices,
        isActive: formData.isActive,
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt) : null
      };

      if (editingAnimation) {
        // Update existing animation
        await updateAnimation(editingAnimation.id, animationData);
        alert('動畫更新成功');
      } else {
        // Create new animation
        await createAnimation(animationData);
        alert('動畫建立成功');
      }

      // Reset form
      handleCancel();
    } catch (error) {
      console.error('Error saving animation:', error);
      alert('操作失敗: ' + error.message);
    }
  };

  const handleEdit = (animation) => {
    setEditingAnimation(animation);
    setFormData({
      companyId: animation.companyId || '',
      animationUrl: animation.animationUrl,
      targetDevices: animation.targetDevices || [],
      isActive: animation.isActive,
      expiresAt: animation.expiresAt ? 
        new Date(animation.expiresAt.seconds * 1000).toISOString().slice(0, 16) : ''
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (animationId) => {
    if (!window.confirm('確定要刪除此動畫嗎？')) {
      return;
    }

    try {
      await deleteAnimation(animationId);
      alert('動畫刪除成功');
    } catch (error) {
      console.error('Error deleting animation:', error);
      alert('刪除失敗: ' + error.message);
    }
  };

  const handleCancel = () => {
    setShowCreateModal(false);
    setEditingAnimation(null);
    setFormData({
      companyId: '',
      animationUrl: '',
      targetDevices: [],
      isActive: true,
      expiresAt: ''
    });
  };

  const handleDeviceSelect = (deviceId) => {
    setFormData(prev => {
      const devices = prev.targetDevices.includes(deviceId)
        ? prev.targetDevices.filter(id => id !== deviceId)
        : [...prev.targetDevices, deviceId];
      return { ...prev, targetDevices: devices };
    });
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.companyName : '未知公司';
  };

  const getDeviceName = (deviceId) => {
    const device = devices.find(d => d.id === deviceId);
    return device ? `${device.deviceName} (${device.deviceCode})` : '未知設備';
  };

  const getTargetDescription = (animation) => {
    if (!animation.targetDevices || animation.targetDevices.length === 0) {
      return '全部設備';
    }
    if (animation.targetDevices.length === 1) {
      return getDeviceName(animation.targetDevices[0]);
    }
    return `${animation.targetDevices.length} 個設備`;
  };

  const isExpired = (animation) => {
    if (!animation.expiresAt) return false;
    const expiryDate = animation.expiresAt.seconds 
      ? new Date(animation.expiresAt.seconds * 1000)
      : new Date(animation.expiresAt);
    return expiryDate < new Date();
  };

  const filteredAnimations = animations.filter(a => {
    if (filterType === 'all') return true;
    if (filterType === 'global') return !a.targetDevices || a.targetDevices.length === 0;
    if (filterType === 'specific') return a.targetDevices && a.targetDevices.length > 0;
    return true;
  });

  const companyDevices = devices.filter(d => d.companyId === formData.companyId);

  if (loading) {
    return (
      <div className="animation-management">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="animation-management">
      <div className="header">
        <h1>動畫管理</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            ← 返回
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            ➕ 新增動畫
          </button>
        </div>
      </div>

      <div className="filters">
        <label>篩選類型:</label>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="filter-select"
        >
          <option value="all">全部動畫</option>
          <option value="global">全公司動畫</option>
          <option value="specific">特定設備</option>
        </select>
      </div>

      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingAnimation ? '編輯動畫' : '新增動畫'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>所屬公司 *</label>
                <select
                  value={formData.companyId}
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value, targetDevices: [] })}
                  required
                >
                  <option value="">請選擇公司</option>
                  {companies.filter(c => c.isActive).map(company => (
                    <option key={company.id} value={company.id}>
                      {company.companyName} ({company.companyCode})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>動畫影片 URL *</label>
                <input
                  type="url"
                  value={formData.animationUrl}
                  onChange={(e) => setFormData({ ...formData, animationUrl: e.target.value })}
                  required
                  placeholder="https://example.com/animation.mp4"
                />
                <small className="help-text">
                  支援格式: MP4, WebM
                </small>
              </div>

              {formData.animationUrl && (
                <div className="form-group">
                  <label>預覽</label>
                  <video
                    src={formData.animationUrl}
                    controls
                    muted
                    style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                  >
                    您的瀏覽器不支援影片播放
                  </video>
                </div>
              )}

              {formData.companyId && (
                <div className="form-group">
                  <label>目標設備</label>
                  <div className="device-list">
                    <div className="device-option">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.targetDevices.length === 0}
                          onChange={() => setFormData({ ...formData, targetDevices: [] })}
                        />
                        全部設備
                      </label>
                    </div>
                    {companyDevices.map(device => (
                      <div key={device.id} className="device-option">
                        <label>
                          <input
                            type="checkbox"
                            checked={formData.targetDevices.includes(device.id)}
                            onChange={() => handleDeviceSelect(device.id)}
                            disabled={formData.targetDevices.length === 0 && !formData.targetDevices.includes(device.id)}
                          />
                          {device.deviceName} ({device.deviceCode})
                        </label>
                      </div>
                    ))}
                  </div>
                  <small className="help-text">
                    不選擇任何設備 = 套用到該公司的全部設備
                  </small>
                </div>
              )}

              <div className="form-group">
                <label>過期時間（選填）</label>
                <input
                  type="datetime-local"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                />
                <small className="help-text">留空 = 永不過期</small>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  啟用此動畫
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingAnimation ? '更新' : '建立'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="animations-list">
        {filteredAnimations.length === 0 ? (
          <div className="empty-state">
            <p>尚未建立任何動畫</p>
            <p className="help-text">點擊上方「新增動畫」按鈕開始建立</p>
          </div>
        ) : (
          <div className="animations-grid">
            {filteredAnimations.map((animation) => (
              <div key={animation.id} className="animation-card">
                <div className="animation-preview">
                  <video
                    src={animation.animationUrl}
                    muted
                    loop
                    autoPlay
                    onError={(e) => { e.target.style.display = 'none'; }}
                  >
                    您的瀏覽器不支援影片
                  </video>
                  {(!animation.isActive || isExpired(animation)) && (
                    <div className="disabled-overlay">
                      {isExpired(animation) ? '已過期' : '已停用'}
                    </div>
                  )}
                </div>
                <div className="animation-info">
                  <h3>{getCompanyName(animation.companyId)}</h3>
                  <div className="info-row">
                    <span className="label">目標:</span>
                    <span className="value">{getTargetDescription(animation)}</span>
                  </div>
                  {animation.expiresAt && (
                    <div className="info-row">
                      <span className="label">過期:</span>
                      <span className={`value ${isExpired(animation) ? 'expired' : ''}`}>
                        {new Date(animation.expiresAt.seconds * 1000).toLocaleString('zh-TW')}
                      </span>
                    </div>
                  )}
                  <div className="info-row">
                    <span className="label">狀態:</span>
                    <span className={`status-badge ${animation.isActive ? 'active' : 'inactive'}`}>
                      {animation.isActive ? '啟用' : '停用'}
                    </span>
                  </div>
                </div>
                <div className="animation-actions">
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => handleEdit(animation)}
                  >
                    編輯
                  </button>
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleDelete(animation.id)}
                  >
                    刪除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AnimationManagement;
